import { useState } from "react";
import { Plus, Search, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const slug = (s) => s.replace(/\W+/g, "-").toLowerCase();
export const chipCls = (active) => `rounded-full border px-3 py-1.5 text-[12px] transition-colors duration-200 ${active ? "border-blue-300 bg-blue-500/45 font-semibold text-white shadow-[0_0_14px_rgba(96,165,250,0.35)]" : "border-white/10 bg-white/[0.03] text-white/60 hover:bg-white/[0.08]"}`;

/** Picked instruments as chips + a grouped, searchable list of every instrument. */
export default function InstrumentPicker({ catalog, value, max, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const groups = catalog.instrument_groups || [{ label: "Instruments", items: catalog.instruments }];
  const toggle = (name) => onChange(value.includes(name) ? value.filter((i) => i !== name) : [...value, name].slice(0, max));
  const match = (name) => name.toLowerCase().includes(q.trim().toLowerCase());
  return (
    <div data-testid="instrument-picker" className="flex flex-wrap gap-2">
      {value.map((name) => (
        <button key={name} type="button" data-testid={`instrument-picked-${slug(name)}`} onClick={() => toggle(name)} className={`${chipCls(true)} inline-flex items-center gap-1`}>
          {name} <X className="h-3 w-3" />
        </button>
      ))}
      <button type="button" data-testid="instrument-picker-open" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-blue-300/50 px-3 py-1.5 text-[12px] text-blue-100 transition-colors hover:bg-blue-500/15">
        <Plus className="h-3.5 w-3.5" /> {value.length ? "Add more" : "Choose instruments"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="instrument-picker-dialog" className="max-h-[88vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0b0e1c] text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Choose instruments</DialogTitle>
            <DialogDescription className="text-white/55">{value.length} of {max} picked — tap again to remove.</DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2">
            <Search className="h-4 w-4 text-white/40" />
            <input data-testid="instrument-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search — e.g. sax, organ, steel pan" className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none" />
          </label>
          <div className="space-y-5">
            {groups.map((g) => {
              const items = g.items.filter(match);
              if (!items.length) return null;
              return (
                <div key={g.label}>
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">{g.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {items.map((name) => {
                      const on = value.includes(name);
                      return <button key={name} type="button" data-testid={`instrument-${slug(name)}`} aria-pressed={on} disabled={!on && value.length >= max} onClick={() => toggle(name)} className={`${chipCls(on)} disabled:cursor-not-allowed disabled:opacity-35`}>{name}</button>;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" data-testid="instrument-picker-done" onClick={() => setOpen(false)} className="mt-2 w-full rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-5 py-3 font-display text-sm font-bold text-white transition-transform hover:scale-[1.01]">Done</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
